/**
 * Dark Mode Toggle Functionality
 */

class DarkModeToggle {
  constructor() {
    this.darkModeKey = 'darkMode';
    this.root = document.documentElement;
    this.body = document.body;
    this.toggle = document.getElementById('dark-mode-toggle');
    this.button = this.toggle?.querySelector('.dark-mode-btn');
    this.storeLocatorObserver = null;
    
    this.init();
  }

  init() {
    // Check for saved dark mode preference or default to light mode
    const savedMode = this.getSavedMode();

    // Apply initial mode - default to light mode if no saved preference
    this.applyMode(savedMode === true);

    requestAnimationFrame(() => {
      this.root.dataset.themeReady = 'true';
    });

    if (!this.toggle || !this.button) return;
    
    // Add event listeners
    this.button.addEventListener('click', () => this.toggleMode());
    
    // Set up store locator monitoring
    this.setupStoreLocatorMonitoring();
    
    // Set up Gorgias chat monitoring
    this.setupGorgiasChatMonitoring();
    
    // Show the toggle after initialization
    this.toggle.style.opacity = '0.9';
  }

  setupStoreLocatorMonitoring() {
    // Monitor for store locator app loading
    const checkForStoreLocator = () => {
      const storeLocatorContainer = document.getElementById('scasl-tab-radius');
      if (storeLocatorContainer) {
        // Apply dark mode if currently active
        if (this.isDarkModeActive()) {
          // Delay to ensure app has finished loading
          setTimeout(() => {
            this.updateStoreLocator(true);
          }, 100);
        }
        
        // Set up mutation observer to watch for changes
        this.setupStoreLocatorObserver(storeLocatorContainer);
      } else {
        // Keep checking if store locator hasn't loaded yet
        setTimeout(checkForStoreLocator, 500);
      }
    };

    // Start checking immediately and also after DOM is fully loaded
    checkForStoreLocator();
    
    // Also check when window loads (in case app loads after DOM ready)
    window.addEventListener('load', () => {
      setTimeout(checkForStoreLocator, 1000);
    });
  }

  setupStoreLocatorObserver(container) {
    if (this.storeLocatorObserver) {
      this.storeLocatorObserver.disconnect();
    }

    this.storeLocatorObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        // Check if style attribute was modified
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          shouldUpdate = true;
        }
        // Check if new elements were added
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate && this.isDarkModeActive()) {
        // Re-apply dark mode styles after a short delay
        setTimeout(() => {
          this.updateStoreLocator(true);
        }, 50);
      }
    });

    // Observe the container and its children
    this.storeLocatorObserver.observe(container, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: true
    });
  }

  toggleMode() {
    const currentMode = this.isDarkModeActive();
    const newMode = !currentMode;
    
    this.applyMode(newMode);
    this.saveMode(newMode);
    
    // Trigger custom event for other scripts
    window.dispatchEvent(new CustomEvent('darkModeToggle', { 
      detail: { darkMode: newMode } 
    }));
  }

  applyMode(isDark) {
    // Keep the root element as the canonical theme state and mirror classes for compatibility.
    this.root.dataset.theme = isDark ? 'dark' : 'light';
    this.root.classList.toggle('dark-mode', isDark);
    this.body.classList.toggle('dark-mode', isDark);
    this.updateCSSVariables(isDark);
    this.switchLogos(isDark);
    
    // Delay store locator update to ensure it runs after any app initialization
    setTimeout(() => {
      this.updateStoreLocator(isDark);
    }, 100);
    
    // Update aria-label for accessibility
    if (this.button) {
      const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';
      this.button.setAttribute('aria-label', label);
      this.button.setAttribute('title', label);
    }
  }

  updateCSSVariables(isDark) {
    const root = this.root;
    
    if (isDark) {
      // Apply dark mode colors using theme settings
      root.style.setProperty('--color-main-background', this.getSettingColor('dark_mode_background', '#1a1a1a'));
      root.style.setProperty('--dark-mode-background', this.getSettingColor('dark_mode_background', '#1a1a1a'));
      root.style.setProperty('--dark-mode-header-background', this.getSettingColor('dark_mode_header_background', '#1e1e1e'));
      root.style.setProperty('--color-text', this.getSettingColor('dark_mode_text', '#e0e0e0'));
      root.style.setProperty('--color-text-secondary', this.getSettingColor('dark_mode_text_secondary', '#b0b0b0'));
      root.style.setProperty('--dark-mode-text-links', this.getSettingColor('dark_mode_text_links', '#ffffff'));
      root.style.setProperty('--color-border', this.getSettingColor('dark_mode_border', '#333333'));
      root.style.setProperty('--color-card-background', this.getSettingColor('dark_mode_card_background', '#1e1e1e'));
    } else {
      // Reset to light mode (remove custom properties to use original CSS)
      root.style.removeProperty('--color-main-background');
      root.style.removeProperty('--dark-mode-background');
      root.style.removeProperty('--dark-mode-header-background');
      root.style.removeProperty('--color-text');
      root.style.removeProperty('--color-text-secondary');
      root.style.removeProperty('--dark-mode-text-links');
      root.style.removeProperty('--color-border');
      root.style.removeProperty('--color-card-background');
      root.style.removeProperty('--color-input-background');
    }
  }

  isDarkModeActive() {
    return this.root.dataset.theme === 'dark';
  }

  updateStoreLocator(isDark) {
    const storeLocatorContainer = document.getElementById('scasl-tab-radius');
    if (!storeLocatorContainer) return;

    if (isDark) {
      // Apply dark mode styles with higher specificity
      this.applyStoreLocatorDarkStyles(storeLocatorContainer);
      
      // Also add a class for CSS targeting
      storeLocatorContainer.classList.add('dark-mode-store-locator');
    } else {
      // Remove dark mode class
      storeLocatorContainer.classList.remove('dark-mode-store-locator');
      
      // Remove any dark mode style overrides
      this.removeStoreLocatorDarkStyles(storeLocatorContainer);
    }
  }

  applyStoreLocatorDarkStyles(container) {
    // Apply styles with setProperty to ensure they override inline styles
    container.style.setProperty('background-color', '#1e1e1e', 'important');
    container.style.setProperty('border-color', '#333333', 'important');
    container.style.setProperty('color', '#e0e0e0', 'important');

    // Update all input fields
    const inputs = container.querySelectorAll('input:not([type="submit"]), select, textarea');
    inputs.forEach(input => {
      input.style.setProperty('background-color', '#2d3748', 'important');
      input.style.setProperty('border-color', '#333333', 'important');
      input.style.setProperty('color', '#e0e0e0', 'important');
    });

    // Update labels
    const labels = container.querySelectorAll('label');
    labels.forEach(label => {
      label.style.setProperty('color', '#e0e0e0', 'important');
    });

    // Update submit button
    const submitButton = container.querySelector('#bh-sl-submit, button[type="submit"]');
    if (submitButton) {
      submitButton.style.setProperty('background-color', '#e61e3c', 'important');
      submitButton.style.setProperty('color', '#ffffff', 'important');
      submitButton.style.setProperty('border-color', '#e61e3c', 'important');
    }

    // Update other buttons
    const otherButtons = container.querySelectorAll('button:not([type="submit"]), .scasl-see-more, .scasl-see-less');
    otherButtons.forEach(button => {
      button.style.setProperty('color', '#ffffff', 'important');
      if (button.style.backgroundColor && button.style.backgroundColor !== 'none') {
        button.style.setProperty('background-color', '#2d3748', 'important');
      }
    });

    // Update any text elements
    const textElements = container.querySelectorAll('span, div, p');
    textElements.forEach(element => {
      if (!element.style.color || element.style.color === 'black' || element.style.color === '#000000') {
        element.style.setProperty('color', '#e0e0e0', 'important');
      }
    });
  }

  removeStoreLocatorDarkStyles(container) {
    // Remove dark mode style properties
    const elementsToReset = container.querySelectorAll('*');
    elementsToReset.forEach(element => {
      // Remove our dark mode overrides but keep original inline styles
      const style = element.getAttribute('style');
      if (style) {
        // This is a simplified approach - in a real scenario you might want to 
        // store and restore original values more carefully
        element.style.removeProperty('background-color');
        element.style.removeProperty('color');
        element.style.removeProperty('border-color');
      }
    });
  }

  switchLogos(isDark) {
    const logos = {
      header: {
        selector: '.header-logo',
        darkUrl: this.getThemeSetting('dark_mode_header_logo')
      },
      footer: {
        selector: '.footer-logo',
        darkUrl: this.getThemeSetting('dark_mode_footer_logo')
      }
    };

    Object.entries(logos).forEach(([type, config]) => {
      const element = document.querySelector(config.selector);
      if (element) {
        this.updateLogo(element, config.darkUrl, isDark, type);
      }
    });
  }

  updateLogo(element, darkUrl, isDark, type) {
    if (!element) return;

    // Find picture and img elements within the logo container
    const pictureElement = element.querySelector('picture');
    const imgElement = element.querySelector('img');

    if (!imgElement) {
      return;
    }

    if (isDark && darkUrl) {
      // Store original values on first dark mode switch
      if (!imgElement.dataset.originalSrc) {
        imgElement.dataset.originalSrc = imgElement.src;
      }

      // Update img src
      imgElement.src = darkUrl;

      // Update all source elements' srcset if picture exists
      if (pictureElement) {
        const sources = pictureElement.querySelectorAll('source');
        sources.forEach((source, index) => {
          if (!source.dataset.originalSrcset) {
            source.dataset.originalSrcset = source.srcset;
          }
          source.srcset = darkUrl;
        });
      }
    } else if (!isDark && imgElement.dataset.originalSrc) {
      // Restore original img src
      imgElement.src = imgElement.dataset.originalSrc;

      // Restore all source elements' srcset
      if (pictureElement) {
        const sources = pictureElement.querySelectorAll('source');
        sources.forEach(source => {
          if (source.dataset.originalSrcset) {
            source.srcset = source.dataset.originalSrcset;
          }
        });
      }
    }
  }

  getThemeSetting(settingName, fallback = null) {
    const sources = [
      () => window.theme?.settings?.[settingName],
      () => window.Shopify?.theme?.settings?.[settingName],
      () => window.settings?.[settingName],
      () => document.querySelector(`meta[name="theme-setting-${settingName}"]`)?.getAttribute('content'),
      () => document.body.dataset[settingName]
    ];

    for (const getSource of sources) {
      const value = getSource();
      if (value && value !== '') {
        return value;
      }
    }

    return fallback;
  }

  getSettingColor(settingName, fallback) {
    // Try to get color from Shopify settings
    const setting = window.theme?.settings?.[settingName];
    return setting || fallback;
  }

  saveMode(isDark) {
    try {
      localStorage.setItem(this.darkModeKey, isDark.toString());
    } catch (e) {
      // Fallback to cookie if localStorage is not available
      document.cookie = `${this.darkModeKey}=${isDark}; path=/; max-age=${365 * 24 * 60 * 60}`;
    }
  }

  getSavedMode() {
    try {
      const saved = localStorage.getItem(this.darkModeKey);
      return saved !== null ? saved === 'true' : null;
    } catch (e) {
      // Fallback to cookie
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === this.darkModeKey) {
          return value === 'true';
        }
      }
      return null;
    }
  }

  setupGorgiasChatMonitoring() {
    // Track found iframes to avoid duplicate processing
    this.processedGorgiasIframes = new Set();
    
    // Cached selectors for better performance
    const gorgiasSelectors = [
      'iframe#chat-window',
      'iframe[class*="gorgias-chat-key"]',
      'iframe[title*="Gorgias"]',
      'iframe[srcdoc*="mountHere"]'
    ];
    
    // Monitor for Gorgias chat iframe loading
    const checkForGorgiasChat = () => {
      let gorgiasIframe = null;
      
      // Use more efficient selector approach
      for (const selector of gorgiasSelectors) {
        const iframes = document.querySelectorAll(selector);
        for (const iframe of iframes) {
          if (!this.processedGorgiasIframes.has(iframe)) {
            gorgiasIframe = iframe;
            this.processedGorgiasIframes.add(iframe);
            break;
          }
        }
        if (gorgiasIframe) break;
      }

      if (gorgiasIframe) {
        // Check current dark mode state and apply immediately
        const isDarkModeActive = this.isDarkModeActive();
        
        if (isDarkModeActive) {
          // Apply dark mode styles with optimized retry schedule
          this.updateGorgiasChat(true, gorgiasIframe);
          
          // Optimized retry intervals
          const retryIntervals = [100, 300, 600, 1000, 2000];
          retryIntervals.forEach(delay => {
            setTimeout(() => this.updateGorgiasChat(true, gorgiasIframe), delay);
          });
        }
        
        // Set up observer to watch for iframe changes
        this.setupGorgiasChatObserver(gorgiasIframe);
        
        // Continue checking for more iframes with reduced frequency
        setTimeout(checkForGorgiasChat, 1000);
      } else {
        // Reduced check frequency when no iframe found
        setTimeout(checkForGorgiasChat, 1000);
      }
    };

    // Optimized MutationObserver with throttling
    let checkScheduled = false;
    const observer = new MutationObserver((mutations) => {
      if (checkScheduled) return;
      
      const hasNewIframe = mutations.some(mutation => 
        mutation.type === 'childList' && 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === Node.ELEMENT_NODE && 
          (node.tagName === 'IFRAME' || node.querySelector?.('iframe'))
        )
      );
      
      if (hasNewIframe) {
        checkScheduled = true;
        setTimeout(() => {
          checkForGorgiasChat();
          checkScheduled = false;
        }, 50);
      }
    });

    // Start observing with optimized config
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Optimized initial check schedule
    const initialChecks = [0, 100, 500, 1000, 2000];
    initialChecks.forEach(delay => {
      setTimeout(checkForGorgiasChat, delay);
    });
    
    // Window load check
    window.addEventListener('load', () => {
      setTimeout(checkForGorgiasChat, 100);
      setTimeout(checkForGorgiasChat, 1000);
    }, { once: true });

    // Optimized dark mode toggle handler
    window.addEventListener('darkModeToggle', (e) => {
      // Use requestAnimationFrame for smoother performance
      requestAnimationFrame(() => {
        gorgiasSelectors.forEach(selector => {
          const iframes = document.querySelectorAll(selector);
          iframes.forEach(iframe => {
            this.updateGorgiasChat(e.detail.darkMode, iframe);
          });
        });
      });
    });
  }

  setupGorgiasChatObserver(iframe) {
    // Create a mutation observer to watch for iframe changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'src' || mutation.attributeName === 'style')) {
          // Re-apply dark mode if active
          if (this.isDarkModeActive()) {
            setTimeout(() => {
              this.updateGorgiasChat(true, iframe);
            }, 100);
          }
        }
      });
    });

    observer.observe(iframe, {
      attributes: true,
      attributeFilter: ['src', 'style']
    });

    // Also observe the parent container for changes
    const parentContainer = iframe.closest('[class*="gorgias"], [id*="gorgias"], .chat-widget, [class*="chat"]');
    if (parentContainer) {
      observer.observe(parentContainer, {
        childList: true,
        subtree: true
      });
    }
  }

  updateGorgiasChat(isDark, iframe) {
    if (!iframe) return;

    try {
      // Enhanced dark mode styles specifically for Gorgias elements
      const darkModeStyles = `
        /* Main conversation container */
        #conversation {
          background-color: ${this.getSettingColor('dark_mode_background', '#333333')} !important;
          color: ${this.getSettingColor('dark_mode_text', '#e0e0e0')} !important;
        }
        
        /* Chat container and wrapper elements */
        .chat-container, .messages-container, .chat-wrapper, .chat-content {
          background-color: ${this.getSettingColor('dark_mode_background', '#333333')} !important;
          color: ${this.getSettingColor('dark_mode_text', '#e0e0e0')} !important;
        }
        
        /* Messages */
        .message, .chat-message, .msg, [class*="message"]:not([data-testid="avatar-status-indicator"]) {
          background-color: ${this.getSettingColor('dark_mode_card_background', '#1e1e1e')} !important;
          color: ${this.getSettingColor('dark_mode_text', '#e0e0e0')} !important;
        }
        #conversation .message-window-iframe-4v4ug1 {
          background-color: #000000 !important;
        }
        #conversation .message-window-iframe-4v4ug1 > p {
          background-color: transparent !important;
        }
        
        /* User messages */
        .message.user, .chat-message.user, .msg.user, .message--user, [class*="message"][class*="user"] {
          background-color: #e61e3c !important;
          color: #ffffff !important;
        }
        
        /* Agent messages */
        .message.agent, .chat-message.agent, .msg.agent, .message--agent, [class*="message"][class*="agent"] {
          background-color: ${this.getSettingColor('dark_mode_card_background', '#1e1e1e')} !important;
          color: ${this.getSettingColor('dark_mode_text', '#e0e0e0')} !important;
        }
        
        /* Input fields */
        input, textarea, .input, .chat-input, [class*="input"] {
          background-color: ${this.getSettingColor('dark_mode_card_background', '#1e1e1e')} !important;
          color: ${this.getSettingColor('dark_mode_text', '#e0e0e0')} !important;
          border-color: ${this.getSettingColor('dark_mode_border', '#333333')} !important;
        }
        
        /* Buttons */
        button, .btn, .button, [class*="button"] {
          background-color: #e61e3c !important;
          color: #ffffff !important;
          border-color: #e61e3c !important;
        }
        
        /* Chat header */
        .header, .chat-header, .conversation-header, [class*="header"] {
          background-color: ${this.getSettingColor('dark_mode_header_background', '#1e1e1e')} !important;
          color: ${this.getSettingColor('dark_mode_text', '#e0e0e0')} !important;
          border-color: ${this.getSettingColor('dark_mode_border', '#333333')} !important;
        }
        
        /* Links */
        a {
          color: ${this.getSettingColor('dark_mode_text_links', '#ffffff')} !important;
        }
        
        /* Text elements */
        p, span, div, label {
          color: ${this.getSettingColor('dark_mode_text', '#e0e0e0')} !important;
        }
        
        /* Scrollbars */
        ::-webkit-scrollbar {
          background-color: ${this.getSettingColor('dark_mode_background', '#333333')} !important;
        }
        
        ::-webkit-scrollbar-thumb {
          background-color: ${this.getSettingColor('dark_mode_border', '#333333')} !important;
        }
        
        /* Catch-all for any remaining elements */
        * {
          border-color: ${this.getSettingColor('dark_mode_border', '#333333')} !important;
        }
      `;

      // Optimized iframe content access with retry logic
      const waitForIframeContent = (attempts = 0) => {
        if (attempts > 15) return;

        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          
          if (iframeDoc && (iframeDoc.readyState === 'complete' || iframeDoc.readyState === 'interactive')) {
            const mountPoint = iframeDoc.getElementById('mountHere') || 
                             iframeDoc.querySelector('.frame-root') ||
                             iframeDoc.getElementById('conversation') ||
                             iframeDoc.querySelector('.conversation');
            
            if (mountPoint || attempts > 10) {
              // Remove existing styles
              const existingStyle = iframeDoc.getElementById('dark-mode-gorgias');
              if (existingStyle) existingStyle.remove();

              if (isDark) {
                // Add dark mode styles
                const styleElement = iframeDoc.createElement('style');
                styleElement.id = 'dark-mode-gorgias';
                styleElement.innerHTML = darkModeStyles;
                
                (iframeDoc.head || iframeDoc.body || iframeDoc.documentElement).appendChild(styleElement);
                
                // Apply direct styles to conversation element
                setTimeout(() => {
                  const conversation = iframeDoc.getElementById('conversation');
                  if (conversation) {
                    conversation.style.setProperty('background-color', this.getSettingColor('dark_mode_background', '#333333'), 'important');
                    conversation.style.setProperty('color', this.getSettingColor('dark_mode_text', '#e0e0e0'), 'important');
                  }
                }, 50);
                
              } else {
                // Light mode cleanup
                const elementsToClean = [
                  iframeDoc.getElementById('conversation'),
                  iframeDoc.querySelector('.conversation'),
                  iframeDoc.getElementById('mountHere'),
                  iframeDoc.querySelector('.frame-root'),
                  iframeDoc.body,
                  iframeDoc.documentElement
                ].filter(Boolean);
                
                elementsToClean.forEach(element => {
                  element.style.removeProperty('background-color');
                  element.style.removeProperty('color');
                });
                
                // Clean up other elements with our colors
                const styledElements = iframeDoc.querySelectorAll('[style*="background-color"], [style*="color"]');
                styledElements.forEach(element => {
                  const bgColor = element.style.backgroundColor;
                  const textColor = element.style.color;
                  
                  if (bgColor && (bgColor.includes('#333333') || bgColor.includes('#1e1e1e') || bgColor.includes('#e61e3c'))) {
                    element.style.removeProperty('background-color');
                  }
                  if (textColor && (textColor.includes('#e0e0e0') || textColor.includes('#ffffff'))) {
                    element.style.removeProperty('color');
                  }
                });
              }
              return;
            }
          }
          
          // Retry with optimized delay
          setTimeout(() => waitForIframeContent(attempts + 1), Math.min(100 + (attempts * 30), 400));
          
        } catch (e) {
          // PostMessage fallback for cross-origin iframes
          if (attempts > 3 && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'DARK_MODE_UPDATE',
              isDark: isDark,
              styles: isDark ? darkModeStyles : ''
            }, '*');
          }
          
          setTimeout(() => waitForIframeContent(attempts + 1), Math.min(100 + (attempts * 30), 400));
        }
      };

      waitForIframeContent();

      // Apply container styles
      if (isDark) {
        iframe.classList.add('dark-mode-chat');
      } else {
        iframe.style.removeProperty('background-color');
        iframe.classList.remove('dark-mode-chat');
      }

      // Apply parent container styles
      const iframeContainer = iframe.parentElement;
      if (iframeContainer) {
        if (isDark) {
          iframeContainer.style.setProperty('background-color', this.getSettingColor('dark_mode_background', '#333333'), 'important');
          iframeContainer.style.setProperty('border-color', this.getSettingColor('dark_mode_border', '#333333'), 'important');
          iframeContainer.classList.add('dark-mode-chat');
        } else {
          iframeContainer.style.removeProperty('background-color');
          iframeContainer.style.removeProperty('border-color');
          iframeContainer.classList.remove('dark-mode-chat');
        }
      }

    } catch (error) {
      // Silent error handling for production
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new DarkModeToggle());
} else {
  new DarkModeToggle();
}
